import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Form } from "../../../../components/ui/form";
import { Button } from "../../../../components/ui/button";
import { SheetTitle } from "../../../../components/ui/sheet";
import { useToast } from "../../../../hooks/use-toast";
import { FormInput } from "../../../../components/form-components/form-input.components";
import { useEffect } from "react";
import Spinner from "../../../../components/spinner.components";
import InfoIcon from "../../../../assets/svgs/common/info.svg";
import { useAddBankAccountHttp } from "../../../../hooks/http/admin/vendors/payouts/add-bank-account.admin.http";
import { handleHttpError } from "../../../../utils/utils";
import { useParams } from "react-router-dom";
import usePayoutMethodsStore from "../../../../store/payout-methods.store";

const FormSchema = z.object({
  bankAccNo: z.string().min(10, { message: "Enter valid bank account number" }),
  ifsc: z.string().min(11, { message: "Enter valid IFSC code" }),
});

export default function AddPayoutSliderAdminPartial() {
  const { execute: addBankAccountHttp, fetching: addingBankAccount } =
    useAddBankAccountHttp();
  const { addPayoutMethod } = usePayoutMethodsStore();

  const { toast } = useToast();
  const { vendorId } = useParams();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      bankAccNo: "",
      ifsc: "",
    },
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (!vendorId) {
      return;
    }
    const response = await addBankAccountHttp({
      body: {
        ifscCode: data.ifsc,
        accountNumber: data.bankAccNo,
      },
      pathParams: {
        vendorId,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    addPayoutMethod(response.data.payoutMethod);

    document.getElementById("closeSheet")?.click();
  }

  // Show form validation errors as toast
  useEffect(() => {
    if (form.formState.errors) {
      const key = Object.keys(form.formState.errors)[0];
      if (key) {
        toast({
          content:
            form.formState.errors[key as keyof typeof form.formState.errors]
              ?.message || "",
          toastType: "error",
        });
      }
    }
  }, [form.formState.errors, toast]);

  return (
    <div className="flex flex-col w-full h-full">
      <SheetTitle className="p-5 border-b border-border font-semibold text-2xl text-white">
        Add Payout details
      </SheetTitle>

      <div className="flex flex-col gap-10 p-5 w-full h-full">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col justify-between gap-8 w-full h-full"
          >
            <div className="flex flex-col gap-6 w-full">
              <FormInput
                name="bankAccNo"
                label="Enter Bank account number"
                placeholder="Account number"
                form={form}
                type="text"
              />
              <FormInput
                name="ifsc"
                label="Enter IFSC code"
                placeholder="IFSC code"
                form={form}
                type="text"
              />
            </div>
            <div className="flex flex-col gap-5 w-full">
              <Button className="flex items-center gap-2 bg-tertiarybuttonbg py-7 border border-border">
                <InfoIcon />
                Add bank account registered with the same name as pan
              </Button>

              {/* Submit Button */}
              <Button
                disabled={addingBankAccount || !form.formState.isValid}
                type="submit"
                className="flex gap-2 bg-primarybuttonbg py-8 w-full font-semibold text-primarybuttontext"
              >
                {addingBankAccount ? <Spinner width={20} /> : "Add payout →"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
